// src/components/LanguageSelectDropdown.tsx
import React from 'react';
import { Icon } from '@/components/ui/Icon';

export interface Language {
    code: string;
    name: string;
    flag: string;

}

interface LanguageSelectDropdownProps {
    availableLanguages: Language[];
    onSelect: (lang: Language) => void;
    triggerText?: string;
    triggerIcon?: React.ComponentProps<typeof Icon>['name'];
    triggerClassName?: string;
    hideIcon?: boolean;
}

const DEFAULT_CHIP_CLASSNAME =
    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-secondary-50 text-secondary-700 border border-secondary-200 hover:bg-secondary-100 transition-colors cursor-pointer select-none disabled:cursor-not-allowed disabled:opacity-50";

/** One chip per not-yet-added language — click a chip to add it directly, no separate open/pick step. */
export const LanguageSelectDropdown = ({ availableLanguages, onSelect, triggerText = "Add language variant", triggerIcon = "translate" as React.ComponentProps<typeof Icon>['name'], triggerClassName, hideIcon }: LanguageSelectDropdownProps) => {
    if (availableLanguages.length === 0) {
        return (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-bg/50 text-muted border border-secondary/10 cursor-not-allowed min-w-[160px] select-none">
                {!hideIcon && <Icon name={triggerIcon} size={16} />}
                <span>All languages added</span>
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {!hideIcon && (
                <span className="flex items-center gap-1 text-[11px] font-medium text-muted">
                    <Icon name={triggerIcon} size={12} />
                    {triggerText}
                </span>
            )}
            {availableLanguages.map((lang) => (
                // Real <button>, not a <div onClick> — so an ancestor
                // <fieldset disabled> (the wizard's read-only mode) disables
                // it natively instead of leaving it clickable.
                <button
                    key={lang.code}
                    type="button"
                    onClick={() => onSelect(lang)}
                    className={triggerClassName || DEFAULT_CHIP_CLASSNAME}
                >
                    <span className="text-sm">{lang.flag}</span>
                    <span>{lang.name}</span>
                </button>
            ))}
        </div>
    );
};
